import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicCourse, CourseContent, CourseSessionPublic } from '@/types/course';
import { formatPrice, formatPriceSimple } from '@/utils/courseFormat';
import { formatDbDateVi, isClassroomOpenForEnrollment } from '@/utils/dbDateDisplay';
import { RegistrationApi, PromotionInfo } from '@/services/registrationApi';
import apiClient from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useRedirect } from '@/hooks/useRedirect';
import CourseRegistrationForm from '@/components/registration/CourseRegistrationForm';
import CourseContentTree from '@/components/course/CourseContentTree';
import CourseSessionList from '@/components/course/CourseSessionList';
import CoursePurchaseCard from '@/components/course/CoursePurchaseCard';
import CourseInstructorSection from '@/components/course/CourseInstructorSection';
import CourseStickyCta from '@/components/course/landing/CourseStickyCta';
import CourseGiftPromoBanner, {
  GiftOfferAsGift,
  GiftOfferAsSource,
} from '@/components/course/CourseGiftPromoBanner';
import KmPromoIcon from '@/components/course/KmPromoIcon';
import BaProcessFlowCarousel from '@/components/course/BaProcessFlowCarousel';
import { isAi4baCourseCode } from '@/data/ai4baBaProcessFlows';
import { CourseApi } from '@/services/courseApi';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  PlayIcon,
  ComputerDesktopIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const ENROLLED_CTA_LABEL = 'Vào lớp của tôi';

interface CourseDetailProps {
  course: PublicCourse & {
    courseContent?: CourseContent[];
    contentOverview?: CourseContent[];
    courseSessions?: CourseSessionPublic[];
  };
  onRegisterClick?: (courseId: number) => void;
  className?: string;
}

const HTML_PROSE =
  'text-m2-on-surface-variant leading-relaxed [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-2';

function stripHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

/** Parse "Bạn sẽ học được gì": one item per line (or legacy HTML <li>). */
function parseLearnOutcomes(raw: string): string[] {
  if (!raw?.trim()) return [];
  const liMatches = [...raw.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (liMatches.length > 0) {
    return liMatches
      .map((m) => stripHtmlEntities(m[1].replace(/<[^>]+>/g, '')))
      .filter(Boolean);
  }
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split(/\r?\n/)
    .map((line) => stripHtmlEntities(line))
    .filter(Boolean);
}

const SESSION_METHODS = new Set(['ONLINE', 'OFFLINE', 'HYBRID']);

const CourseDetail = ({ course, onRegisterClick, className = '' }: CourseDetailProps) => {
  const { isAuthenticated } = useAuthStore();
  const { redirectToLogin } = useRedirect();
  const navigate = useNavigate();

  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [promotion, setPromotion] = useState<PromotionInfo | null>(null);
  const [loadingPromotion, setLoadingPromotion] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [related, setRelated] = useState<PublicCourse[]>([]);
  const [giftAsSource, setGiftAsSource] = useState<GiftOfferAsSource[]>([]);
  const [giftAsGift, setGiftAsGift] = useState<GiftOfferAsGift[]>([]);

  const landing = course.landingSections;
  const heroHeadline = landing?.heroHeadline || course.name;
  const heroSub = landing?.heroSubheadline || '';
  const methodUpper = (course.method || '').toUpperCase();
  const useSessions = SESSION_METHODS.has(methodUpper);
  /** Khóa video tự học: luôn đăng ký được, không phụ thuộc khai giảng / lớp sắp mở */
  const isVideoSelfPaced = methodUpper === 'VIDEO';

  useEffect(() => {
    if (!isAuthenticated || !course?.id) {
      setIsEnrolled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.get('/student/my-courses');
        const data = response.data;
        if (cancelled) return;
        if (data.success && Array.isArray(data.data)) {
          setIsEnrolled(data.data.some((c: { id: number }) => c.id === course.id));
        } else {
          setIsEnrolled(false);
        }
      } catch {
        if (!cancelled) setIsEnrolled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, course?.id]);

  useEffect(() => {
    const loadPromotionInfo = async () => {
      if (!isAuthenticated || !course || isEnrolled) return;
      try {
        setLoadingPromotion(true);
        const promotionInfo = await RegistrationApi.getBestPromotion(course.id);
        setPromotion(promotionInfo);
      } catch {
        setPromotion({
          promotionId: null,
          promotion_user_Id: null,
          discountAmount: 0,
          fee: course.fee,
          orgFee: course.fee,
        });
      } finally {
        setLoadingPromotion(false);
      }
    };
    loadPromotionInfo();
  }, [isAuthenticated, course, isEnrolled]);

  const primaryAudience = course.audiences?.find((a) => a.isPrimary) || course.audiences?.[0];

  useEffect(() => {
    const code = primaryAudience?.code;
    if (!code) return;
    let cancelled = false;
    CourseApi.getCourses({ audience: code, limit: 6 })
      .then((res) => {
        if (!cancelled) {
          setRelated(res.courses.filter((c) => c.id !== course.id).slice(0, 3));
        }
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [primaryAudience?.code, course.id]);

  useEffect(() => {
    if (!course?.id && !course?.hashId) return;
    let cancelled = false;
    const key = course.hashId || String(course.id);
    CourseApi.getGiftOffers(key)
      .then((offers) => {
        if (cancelled) return;
        setGiftAsSource(offers.asSource || []);
        setGiftAsGift(offers.asGift || []);
      })
      .catch(() => {
        if (!cancelled) {
          setGiftAsSource([]);
          setGiftAsGift([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [course.id, course.hashId]);

  const priceInfo = formatPrice(course?.fee ?? 0);
  const hasGiftPromo = giftAsSource.length > 0 || giftAsGift.length > 0;
  /** Đã học khóa chính (A) — vẫn hiện KM để giới thiệu bạn bè */
  const isSourceEnrolledForGift = isEnrolled && giftAsSource.length > 0;
  /**
   * Chỉ khi khóa đang xem là khóa chính (A tặng B) thì KM tặng khóa ưu tiên,
   * ẩn KM giảm giá khác. Trên khóa được tặng (B): vẫn hiện KM promotion như cũ
   * + hiển thị thêm ưu đãi combo.
   */
  const showDiscountPromo =
    giftAsSource.length === 0 &&
    isAuthenticated &&
    !!promotion &&
    promotion.discountAmount > 0;

  const displayPrice = useMemo(() => {
    if (showDiscountPromo && promotion) {
      return formatPriceSimple(promotion.fee);
    }
    if (typeof priceInfo === 'string') return priceInfo;
    return priceInfo.current;
  }, [showDiscountPromo, promotion, priceInfo]);

  if (!course) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Error: Course data not available</p>
      </div>
    );
  }

  const getMethodConfig = (method: string) => {
    const configs: Record<string, { icon: typeof PlayIcon; label: string }> = {
      VIDEO: { icon: PlayIcon, label: 'Video' },
      ONLINE: { icon: ComputerDesktopIcon, label: 'Live Online' },
      OFFLINE: { icon: BuildingOfficeIcon, label: 'Offline' },
      HYBRID: { icon: DevicePhoneMobileIcon, label: 'Hybrid' },
    };
    return configs[method.toUpperCase()] || configs.VIDEO;
  };

  const methodConfig = getMethodConfig(course.method);
  const MethodIcon = methodConfig.icon;

  const upcomingClasses =
    isVideoSelfPaced
      ? []
      : course.upcomingClasses && Array.isArray(course.upcomingClasses)
        ? course.upcomingClasses.filter(
            (classroom) => classroom.startDate && isClassroomOpenForEnrollment(classroom.startDate)
          )
        : [];

  const handlePrimaryClick = () => {
    if (isEnrolled) {
      navigate(`/student/courses/${course.id}`);
      return;
    }
    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }
    if (onRegisterClick) {
      onRegisterClick(course.id);
    } else {
      setShowRegistrationForm(true);
    }
  };

  const showBaProcess = isAi4baCourseCode(course.code);

  const navItems = [
    { id: 'gioi-thieu', label: 'Giới thiệu' },
    showBaProcess ? { id: 'quy-trinh-ba', label: 'Bộ Skill' } : null,
    { id: 'noi-dung', label: 'Nội dung' },
    useSessions && upcomingClasses.length > 0 ? { id: 'lich-hoc', label: 'Lịch học' } : null,
    { id: 'mentor', label: 'Mentor' },
    landing?.faqs?.length ? { id: 'faq', label: 'FAQ' } : null,
  ].filter(Boolean) as { id: string; label: string }[];

  const nextClassLabel = upcomingClasses[0]
    ? `${upcomingClasses[0].name || upcomingClasses[0].code} · ${
        upcomingClasses[0].startDate
          ? formatDbDateVi(upcomingClasses[0].startDate)
          : ''
      }`
    : null;

  const priceNode: ReactNode = isEnrolled ? (
    <p className="text-2xl font-bold text-m2-primary">Đã đăng ký</p>
  ) : loadingPromotion && isAuthenticated && giftAsSource.length === 0 ? (
    <p className="text-m2-on-surface-variant">Đang tải khuyến mãi...</p>
  ) : showDiscountPromo && promotion ? (
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-3xl font-bold text-m2-primary">{formatPriceSimple(promotion.fee)}</span>
        <span className="text-base text-m2-on-surface-variant line-through">
          {formatPriceSimple(promotion.orgFee)}
        </span>
        <span className="bg-red-100 text-m2-error text-xs font-medium px-2 py-0.5 rounded">
          KHUYẾN MÃI
        </span>
      </div>
      <p className="text-sm text-m2-secondary font-medium">
        Tiết kiệm {formatPriceSimple(promotion.discountAmount)}
      </p>
    </div>
  ) : typeof priceInfo === 'string' ? (
    <p className="text-3xl font-bold text-m2-secondary">{priceInfo}</p>
  ) : (
    <p className="text-3xl font-bold text-m2-on-surface">{priceInfo.current}</p>
  );

  const renderCurriculum = () => {
    if (useSessions) {
      const sessions = course.courseSessions || [];
      if (sessions.length > 0) {
        return <CourseSessionList sessions={sessions} />;
      }
      if (course.content) {
        return <div className={HTML_PROSE} dangerouslySetInnerHTML={{ __html: course.content }} />;
      }
      return <p className="text-m2-on-surface-variant">Nội dung sẽ được cập nhật sớm.</p>;
    }

    if (
      (course.courseContent && course.courseContent.length > 0) ||
      (course.contentOverview && course.contentOverview.length > 0)
    ) {
      return (
        <CourseContentTree content={course.courseContent || course.contentOverview || []} />
      );
    }
    if (course.content) {
      return <div className={HTML_PROSE} dangerouslySetInnerHTML={{ __html: course.content }} />;
    }
    return <p className="text-m2-on-surface-variant">Nội dung sẽ được cập nhật sớm.</p>;
  };

  /** Render lịch học cụ thể của các lớp đang mở */
  const renderScheduleSection = () => {
    if (!useSessions || upcomingClasses.length === 0) return null;
    return (
      <section id="lich-hoc">
        <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Lịch học</h2>
        <div className="space-y-4">
          {upcomingClasses.map((cls) => (
            <div
              key={cls.id}
              className="rounded-xl border border-m2-outline-variant bg-m2-surface-lowest p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-base font-bold text-m2-on-surface">
                  {cls.name || cls.code}
                </h3>
                <span className="inline-flex items-center rounded-full bg-eh-mint px-2.5 py-0.5 text-xs font-semibold text-eh-forest-ink">
                  {cls.status === 'UPCOMING' ? 'Sắp khai giảng' : 'Đang mở đăng ký'}
                </span>
              </div>
              <ul className="space-y-2 text-sm">
                {cls.startDate && (
                  <li className="flex items-center gap-2 text-m2-on-surface-variant">
                    <CalendarDaysIcon className="h-4 w-4 shrink-0 text-m2-primary" />
                    <span>
                      <span className="font-medium text-m2-on-surface">Thời gian khoá học:</span>{' '}
                      {formatDbDateVi(cls.startDate)}
                      {cls.endDate && ` — ${formatDbDateVi(cls.endDate)}`}
                    </span>
                  </li>
                )}
                {(cls.studyTime || cls.schedule) && (
                  <li className="flex items-start gap-2 text-m2-on-surface-variant">
                    <ClockIcon className="h-4 w-4 shrink-0 text-m2-primary mt-0.5" />
                    <span>
                      <span className="font-medium text-m2-on-surface">Lịch học hàng tuần:</span>{' '}
                      {cls.studyTime || cls.schedule}
                    </span>
                  </li>
                )}
                {cls.address && (
                  <li className="flex items-start gap-2 text-m2-on-surface-variant">
                    <MapPinIcon className="h-4 w-4 shrink-0 text-m2-primary mt-0.5" />
                    <span>
                      <span className="font-medium text-m2-on-surface">Địa điểm:</span>{' '}
                      {cls.address}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const purchaseCard = (
    <div className="lg:sticky lg:top-24 space-y-6">
      <CoursePurchaseCard
        priceNode={priceNode}
        methodLabel={methodConfig.label}
        timeInHour={course.timeInHour}
        timeInLesson={course.timeInLesson}
        upcomingClasses={upcomingClasses}
        isSelfPaced={isVideoSelfPaced}
        onRegister={handlePrimaryClick}
        ctaLabel={isEnrolled ? ENROLLED_CTA_LABEL : 'Đăng ký khóa học'}
        isEnrolled={isEnrolled}
      />
      {hasGiftPromo ? (
        <div id="uu-dai" className="space-y-3" aria-label="Ưu đãi combo">
          <CourseGiftPromoBanner
            asSource={giftAsSource}
            asGift={giftAsGift}
            onRegisterCombo={isSourceEnrolledForGift ? undefined : handlePrimaryClick}
            hideSourceFee
            currentCourseName={course.name}
            isSourceEnrolled={isSourceEnrolledForGift}
            sourceCourseName={course.name}
            sourceCourseHashId={course.hashId}
            sourceCourseId={course.id}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={`pb-24 lg:pb-8 ${className}`}>
      <section id="gioi-thieu" className="bg-eh-cream text-eh-charcoal">
        <div className="eh-container py-12 lg:py-16">
          <div className="grid grid-cols-1 items-center overflow-hidden rounded-eh bg-eh-keylime lg:grid-cols-2">
            <div className="px-7 py-10 md:px-10 md:py-12">
              <div className="mb-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-eh-pill border border-eh-forest-ink/15 bg-eh-cream px-2.5 py-1 text-xs font-medium text-eh-forest-ink">
                  <MethodIcon className="h-3.5 w-3.5" />
                  {methodConfig.label}
                </span>
                {course.audiences?.map((a) => (
                  <span
                    key={a.id}
                    className="rounded-eh-pill bg-eh-sage px-2.5 py-1 text-xs font-medium text-eh-forest-ink"
                  >
                    {a.name}
                  </span>
                ))}
              </div>
              <h1 className="eh-display text-[clamp(32px,5vw,52px)] tracking-tight text-eh-forest-ink">
                {heroHeadline}
              </h1>
              {heroSub && <p className="mt-4 text-lg leading-relaxed text-eh-muted">{heroSub}</p>}
              {isVideoSelfPaced ? (
                <div className="mt-4 m2-running-border">
                  <div className="m2-running-border__inner text-sm text-eh-muted">
                    <GlobeAltIcon className="h-4 w-4 shrink-0 text-eh-forest-ink" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-eh-forest-ink sm:text-sm">
                      Học mọi chỗ, mọi nơi
                    </span>
                    <span className="text-eh-border">·</span>
                    <span className="font-medium text-eh-charcoal">
                      Video tự học — đăng ký bất cứ lúc nào
                    </span>
                  </div>
                </div>
              ) : (
                nextClassLabel && (
                  <div className="mt-4 m2-running-border">
                    <div className="m2-running-border__inner text-sm text-eh-muted">
                      <CalendarDaysIcon className="h-4 w-4 shrink-0 text-eh-forest-ink" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-eh-forest-ink sm:text-sm">
                        Khai giảng gần nhất
                      </span>
                      <span className="text-eh-border">·</span>
                      <span className="font-medium text-eh-charcoal">{nextClassLabel}</span>
                    </div>
                  </div>
                )
              )}
              {hasGiftPromo && giftAsSource.length > 0 ? (
                <div className="mt-5 flex flex-col gap-2">
                  {giftAsSource.map((g) => (
                    <Link
                      key={`hero-gift-${g.giftCourseId}`}
                      to={`/courses/${g.giftCourseHashId || g.giftCourseId}`}
                      className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-eh border border-eh-border bg-eh-cream/90 px-3 py-2 text-sm shadow-eh-soft transition-opacity hover:opacity-90"
                    >
                      <KmPromoIcon size="sm" className="!h-5 !w-5" />
                      <span className="font-medium text-eh-charcoal">Tặng kèm</span>
                      <span className="min-w-0 font-semibold text-eh-forest-ink underline underline-offset-2">
                        {g.giftCourseName}
                      </span>
                      <span className="inline-flex items-baseline gap-1.5">
                        <span className="text-eh-muted line-through">
                          {formatPriceSimple(g.giftCourseFee ?? 0)}
                        </span>
                        <span className="font-bold text-m2-error">0 đ</span>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null}
              <div className="mt-8 flex flex-wrap gap-3">
                <button type="button" onClick={handlePrimaryClick} className="eh-btn">
                  {isEnrolled ? (
                    ENROLLED_CTA_LABEL
                  ) : (
                    <>
                      Đăng ký ngay <span className="eh-arrow">→</span>
                    </>
                  )}
                </button>
                <a href="#noi-dung" className="eh-btn-secondary">
                  Xem nội dung
                </a>
              </div>
            </div>
            <div className="flex min-h-[320px] items-center justify-center bg-eh-keylime p-8 md:min-h-[420px]">
              <div className="relative h-64 w-64 overflow-hidden rounded-full bg-eh-cream shadow-eh-soft sm:h-80 sm:w-80 lg:h-96 lg:w-96">
                {course.imageUrl ? (
                  <img
                    src={course.imageUrl}
                    alt={course.name}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-eh-mint">
                    <PlayIcon className="h-16 w-16 text-eh-forest-ink/40" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <nav className="sticky top-0 z-30 bg-m2-surface-lowest/95 backdrop-blur border-b border-eh-border hidden md:block">
        <div className="eh-container flex items-center gap-6 overflow-x-auto py-3 text-sm">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="whitespace-nowrap text-m2-on-surface-variant hover:text-m2-primary font-medium"
            >
              {item.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="eh-container py-12">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 lg:items-start">
          <div className="space-y-16 order-2 lg:order-1">
            {!!landing?.painPoints?.length && (
              <section id="van-de">
                <h2 className="text-2xl font-bold text-m2-on-surface mb-2">
                  Bạn đang gặp những vấn đề này?
                </h2>
                <p className="text-m2-on-surface-variant mb-6">
                  Khóa học này được thiết kế để giúp bạn giải quyết đúng những điểm nghẽn đó.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {landing.painPoints
                    .filter((p) => p.trim())
                    .map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-3 rounded-eh border border-eh-border bg-m2-surface-lowest p-4 text-m2-on-surface"
                      >
                        <ExclamationTriangleIcon
                          className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
                          aria-hidden
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                </ul>
              </section>
            )}

            {(course.requiredInput || course.suitableObject) && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {course.suitableObject && (
                  <div>
                    <h2 className="text-xl font-bold text-m2-on-surface mb-3">Đối tượng phù hợp</h2>
                    <div
                      className={HTML_PROSE}
                      dangerouslySetInnerHTML={{ __html: course.suitableObject }}
                    />
                  </div>
                )}
                {course.requiredInput && (
                  <div>
                    <h2 className="text-xl font-bold text-m2-on-surface mb-3">Yêu cầu tham gia</h2>
                    <div
                      className={HTML_PROSE}
                      dangerouslySetInnerHTML={{ __html: course.requiredInput }}
                    />
                  </div>
                )}
              </section>
            )}

            {(course.shortDescription || (course.tagNames && course.tagNames.length > 0)) && (
              <section id="hoc-duoc-gi">
                <h2 className="text-2xl font-bold text-m2-on-surface mb-4">Bạn sẽ học được gì</h2>
                {course.shortDescription &&
                  (() => {
                    const items = parseLearnOutcomes(course.shortDescription);
                    if (items.length === 0) {
                      return (
                        <div
                          className={HTML_PROSE}
                          dangerouslySetInnerHTML={{ __html: course.shortDescription }}
                        />
                      );
                    }
                    return (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                        {items.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-m2-on-surface">
                            <svg
                              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              aria-hidden
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-[15px] leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                {course.tagNames && course.tagNames.length > 0 && (
                  <div className={`${course.shortDescription ? 'mt-6' : ''} flex flex-wrap gap-2`}>
                    {course.tagNames.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1.5 rounded-eh-pill text-sm font-medium bg-eh-forest-ink text-eh-cream border border-eh-forest-ink"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </section>
            )}

            {!!landing?.outcomes?.length && (
              <section id="ket-qua">
                <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Sau khóa bạn có</h2>
                <div
                  className={`grid grid-cols-1 gap-4 ${
                    landing.outcomes.length <= 2
                      ? 'md:grid-cols-2'
                      : landing.outcomes.length === 3
                        ? 'md:grid-cols-3'
                        : 'sm:grid-cols-2 lg:grid-cols-4'
                  }`}
                >
                  {landing.outcomes.map((o) => {
                    const isWorkflowE2E = /workflow\s*e2e/i.test(o.title);
                    const canJumpToFlow = isWorkflowE2E && showBaProcess;
                    const CardTag = canJumpToFlow ? 'a' : 'div';
                    const cardProps = canJumpToFlow
                      ? { href: '#quy-trinh-ba' as const }
                      : {};
                    return (
                      <CardTag
                        key={o.title}
                        {...cardProps}
                        className={`rounded-eh border border-eh-border bg-m2-surface-lowest p-5 shadow-eh-soft flex flex-col gap-2 min-h-[7.5rem] ${
                          canJumpToFlow
                            ? 'hover:border-m2-primary/60 hover:shadow-m2-glow transition-shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-m2-primary'
                            : ''
                        }`}
                      >
                        <h3 className="text-base sm:text-lg font-bold text-m2-on-surface leading-snug">
                          {o.title}
                        </h3>
                        {o.detail ? (
                          <p className="text-sm sm:text-[15px] leading-relaxed text-m2-on-surface-variant">
                            {o.detail}
                          </p>
                        ) : null}
                        {canJumpToFlow && (
                          <span className="mt-auto pt-1 text-xs font-semibold text-m2-primary">
                            Xem sơ đồ Workflow E2E →
                          </span>
                        )}
                      </CardTag>
                    );
                  })}
                </div>
              </section>
            )}

            {!!landing?.beforeAfter?.length && (
              <section>
                <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Trước → Sau</h2>
                <div className="overflow-x-auto rounded-xl border border-m2-outline-variant">
                  <table className="min-w-full text-sm">
                    <thead className="bg-m2-surface-low text-left">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Công việc</th>
                        <th className="px-4 py-3 font-semibold">Trước → Sau</th>
                        <th className="px-4 py-3 font-semibold">Tiết kiệm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {landing.beforeAfter.map((row) => (
                        <tr key={row.task} className="border-t border-m2-outline-variant">
                          <td className="px-4 py-3">{row.task}</td>
                          <td className="px-4 py-3">
                            {row.before} → {row.after}
                          </td>
                          <td className="px-4 py-3 font-medium text-m2-secondary">{row.saving}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {showBaProcess && (
              <section
                id="quy-trinh-ba"
                className="eh-panel eh-panel-mint border border-eh-border p-5 sm:p-8 -mx-1"
              >
                <h2 className="text-2xl font-bold text-m2-on-surface mb-2">
                  Bộ Skill theo quy trình
                </h2>
                <p className="text-m2-on-surface-variant mb-6">
                  Workflow E2E — nối task thành hệ thống AI bạn điều phối. Bộ skill và workflow
                  thực hiện full quy trình công việc BA.
                </p>
                <BaProcessFlowCarousel />
              </section>
            )}

            <section id="noi-dung">
              <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Lộ trình / Nội dung</h2>
              {renderCurriculum()}
            </section>

            {renderScheduleSection()}

            {!!landing?.demoUrls?.length && (
              <section>
                <h2 className="text-2xl font-bold text-m2-on-surface mb-4">Demo</h2>
                <ul className="space-y-2">
                  {landing.demoUrls.map((url) => (
                    <li key={url}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-m2-primary hover:underline break-all"
                      >
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <CourseInstructorSection
              instructor={landing?.instructor}
              lecturerHtml={course.lecturer}
              detailIntroduceHtml={course.detailIntroduce}
            />

            {!!landing?.faqs?.length && (
              <section id="faq">
                <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Câu hỏi thường gặp</h2>
                <div className="divide-y divide-m2-outline-variant border border-m2-outline-variant rounded-xl overflow-hidden">
                  {landing.faqs.map((faq, idx) => (
                    <div key={`${faq.q}-${idx}`} className="bg-m2-surface-container">
                      <button
                        type="button"
                        className="w-full text-left px-5 py-4 font-medium text-m2-on-surface hover:bg-m2-surface-low flex justify-between gap-4"
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      >
                        {faq.q}
                        <span className="text-m2-outline">{openFaq === idx ? '−' : '+'}</span>
                      </button>
                      {openFaq === idx && (
                        <div className="px-5 pb-4 text-sm text-m2-on-surface-variant whitespace-pre-wrap">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {related.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-m2-on-surface mb-6">Khóa liên quan</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {related.map((c) => (
                    <Link
                      key={c.id}
                      to={`/courses/${c.hashId ?? c.id}`}
                      className="rounded-xl border border-m2-outline-variant p-4 hover:border-m2-primary/40 hover:shadow-sm transition"
                    >
                      <div className="font-semibold text-m2-on-surface line-clamp-2">{c.name}</div>
                      <div className="mt-2 text-sm text-m2-primary">
                        {formatPriceSimple(c.fee)}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <div className="mb-10 lg:mb-0 order-1 lg:order-2">{purchaseCard}</div>
        </div>
      </div>

      <CourseStickyCta
        priceLabel={
          isEnrolled
            ? 'Đã đăng ký'
            : loadingPromotion && isAuthenticated && giftAsSource.length === 0
              ? '...'
              : String(displayPrice)
        }
        priceCaption={isEnrolled ? 'Trạng thái' : 'Học phí'}
        ctaLabel={isEnrolled ? ENROLLED_CTA_LABEL : 'Đăng ký ngay'}
        onRegister={handlePrimaryClick}
      />

      {showRegistrationForm && (
        <CourseRegistrationForm
          course={course}
          onClose={() => setShowRegistrationForm(false)}
        />
      )}
    </div>
  );
};

export default CourseDetail;
